import { faCheck } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	THEME_AWARE_COLORS,
	isLegacyHexColor,
	resolveThemeColor,
	type ThemeAwareColor,
} from '../../constants/theme-aware-colors';
import { useTheme, useThemeColors } from '../../providers/ThemeProvider';
import { cn } from '../../utils/cn';

interface ThemeAwareColorTilesProps {
	/** Color ID (e.g., 'blue') or legacy hex (e.g., '#3B82F6') */
	value: string | null;
	/** Called when a color is selected. Returns the color ID. */
	onChange: (colorId: string) => void;
	/** Optional label above the tiles */
	label?: string;
	/** Whether the selector is disabled */
	disabled?: boolean;
	/** Additional className for the container */
	className?: string;
}

/**
 * A tiled color selector that provides theme-aware colors.
 * Colors automatically adapt to light/dark mode.
 *
 * Stores color IDs (e.g., 'blue', 'green') instead of hex values.
 * Legacy hex values are displayed but new selections always use IDs.
 */
export function ThemeAwareColorTiles({
	value,
	onChange,
	label,
	disabled = false,
	className,
}: ThemeAwareColorTilesProps) {
	const colors = useThemeColors();
	const { isDark } = useTheme();

	// Check if current value is a legacy hex (for display purposes)
	const isLegacy = isLegacyHexColor(value);

	// Get the resolved hex for the current theme (for comparison)
	const resolvedValue = resolveThemeColor(value, isDark);

	// Find which color ID is selected (if any)
	const selectedColorId = isLegacy
		? null
		: (THEME_AWARE_COLORS.find((c) => c.id === value)?.id ?? null);

	const handleColorClick = (color: ThemeAwareColor) => {
		if (disabled) {
			return;
		}

		onChange(color.id);
	};

	return (
		<div className={cn('flex flex-col gap-1', className)}>
			{label != null && (
				<label
					className={cn(
						'block text-xs font-medium',
						colors.classes.textSecondary,
					)}
				>
					{label}
				</label>
			)}

			<div className="grid grid-cols-6 gap-1.5">
				{THEME_AWARE_COLORS.map((color) => {
					const isSelected = selectedColorId === color.id;
					const displayColor = isDark ? color.dark : color.light;

					// Determine if checkmark should be light or dark based on color luminance
					const isLightColor = isColorLight(displayColor);

					return (
						<button
							key={color.id}
							type="button"
							onClick={() => {
								handleColorClick(color);
							}}
							disabled={disabled}
							title={color.name}
							className={cn(
								'relative h-8 w-full rounded-md border-2 transition-all',
								'focus:outline-none focus:ring-2 focus:ring-offset-1',
								colors.classes.focusRingPrimary,
								isSelected
									? 'border-success-tint dark:border-white scale-105 shadow-md'
									: cn(colors.classes.borderDefault, 'hover:scale-105'),
								disabled && 'cursor-not-allowed opacity-50',
							)}
							style={{ backgroundColor: displayColor }}
						>
							{isSelected && (
								<FontAwesomeIcon
									icon={faCheck}
									className={cn(
										'absolute inset-0 m-auto text-xs',
										isLightColor ? 'text-default' : 'text-white',
									)}
								/>
							)}
						</button>
					);
				})}
			</div>

			{/* Show legacy hex indicator if applicable */}
			{isLegacy && value != null && (
				<div
					className={cn(
						'flex items-center gap-2 mt-1 text-xs',
						colors.classes.textSecondary,
					)}
				>
					<div
						className="w-4 h-4 rounded border"
						style={{ backgroundColor: value }}
					/>
					<span>{value} (custom)</span>
				</div>
			)}
		</div>
	);
}

/**
 * Determine if a hex color is light (for choosing checkmark color).
 * Uses relative luminance calculation.
 */
function isColorLight(hexColor: string): boolean {
	// Remove # if present
	const hex = hexColor.replace('#', '');

	// Parse RGB
	const r = parseInt(hex.substring(0, 2), 16);
	const g = parseInt(hex.substring(2, 4), 16);
	const b = parseInt(hex.substring(4, 6), 16);

	// Calculate relative luminance (simplified)
	const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

	return luminance > 0.5;
}

ThemeAwareColorTiles.displayName = 'ThemeAwareColorTiles';
